/**
 * Tabular badge display and chapter-number helpers.
 *
 * @file badgeDisplayUtils.js
 */

import {
  asCoverageArray,
  collectLeafPageIdsFromSectionIds,
  findNodeById,
  toNodeId,
  collectLeafIds,
  findParentChapterForPageInToc,
  filterIdsUnderChapter,
  getChapterRefId,
  getChapterTitleOrEmpty,
  getStoredChapterTitles
} from './coverageDuplicationUtils';

/* --- Badge title resolution --- */

function isChapterFullySelected(chapterId, selectedLeafIds, toc) {
  const chapterNode = findNodeById(toc, chapterId);
  const allLeaves = collectLeafIds(chapterNode);
  if (!allLeaves.length) return false;
  const selected = new Set(selectedLeafIds);

  return allLeaves.every((id) => selected.has(id));
}

function getBadgeTitlesForChapter(chapterId, pageIds, sectionIds, toc) {
  const chapterSections = sectionIds.filter(
    (id) => findParentChapterForPageInToc(id, toc) === chapterId
  );
  const chapterPages = filterIdsUnderChapter(pageIds, chapterId, toc);
  const leafPageIds = chapterPages.length > 0
    ? chapterPages
    : collectLeafPageIdsFromSectionIds(chapterSections, toc);
  const titleNodeIds = chapterPages.length > 0 ? chapterPages : chapterSections;

  if (!leafPageIds.length && !titleNodeIds.length) {
    return getChapterTitleOrEmpty(chapterId, toc);
  }
  if (isChapterFullySelected(chapterId, leafPageIds, toc)) {
    return getChapterTitleOrEmpty(chapterId, toc);
  }

  return titleNodeIds
    .map((id) => findNodeById(toc, id)?.title)
    .filter(Boolean);
}

function resolveCoverageBadgeTitles(coverage, toc) {
  const sectionIds = asCoverageArray(coverage.sectionIds);
  const pages = asCoverageArray(coverage.pages);
  const storedChapters = asCoverageArray(coverage.chapters);
  const uniqueChapterIds = [...new Set([
    ...storedChapters.map(getChapterRefId).filter(Boolean),
    ...sectionIds.map((id) => findParentChapterForPageInToc(id, toc)).filter(Boolean)
  ])];
  const titles = [];

  uniqueChapterIds.forEach((chapterId) => {
    getBadgeTitlesForChapter(chapterId, pages, sectionIds, toc).forEach((title) => {
      if (title && !titles.includes(title)) {
        titles.push(title);
      }
    });
  });

  return titles;
}

/* ------------------------------------------------------------------
 * Public API (exported — bundle + Utils delegates)
 * ------------------------------------------------------------------ */

export function semanticSorting(arr = []) {
  if (!Array.isArray(arr) || arr.length === 0) return [];

  return [...arr].sort((a, b) => {
    const as = a.split('.').map(Number);
    const bs = b.split('.').map(Number);
    const len = Math.max(as.length, bs.length);
    for (let i = 0; i < len; i += 1) {
      const av = as[i] ?? 0;
      const bv = bs[i] ?? 0;
      if (av !== bv) return av - bv;
    }

    return 0;
  });
}

export function resolveDisplayTitles(pageIds, tocData, includeIds = false) {
  const pageSet = new Set(pageIds);
  const titles = [];
  const addToReturnItems = (id, title) => {
    if (!title) return;
    titles.push(includeIds ? { id, title } : title);
  };

  tocData.forEach(chapter => {
    if (typeof chapter !== 'object') return;

    const isChapterInPages = pageSet.has(chapter.id);
    const hasSelectedChild = chapter.children?.some(
      child => pageSet.has(toNodeId(child))
    );
    if (!isChapterInPages && !hasSelectedChild) return;

    if (!chapter.children?.length) {
      addToReturnItems(chapter.id, chapter.title);

      return;
    }

    const allSelected = collectLeafIds(chapter).every(id => pageSet.has(id));
    if (allSelected) {
      addToReturnItems(chapter.id, chapter.title);
    } else {
      chapter.children.forEach(child => {
        const childId = toNodeId(child);
        if (pageSet.has(childId) && child?.title) {
          addToReturnItems(childId, child.title);
        }
      });
    }
  });

  return titles;
}

export function getAssignmentChaptersNumbers(chapters = []) {
  if (chapters.length === 0) return null;

  const nums = semanticSorting([
    ...new Set(
      chapters
        .map(chapter => String(chapter ?? '').match(/\d+(?:\.\d+)*/)?.[0])
        .filter(Boolean)
    )
  ]);

  return nums.length > 0 ? nums : null;
}

/** Chapter numbers for tabular badges when coverage uses pages[] (manual page selection). */
function getChapterNumbersFromPagesCoverage(textbookCoverage, safeToc) {
  const pages = asCoverageArray(textbookCoverage?.pages);
  const storedChapters = asCoverageArray(textbookCoverage?.chapters);
  let chapterTitles = [];

  if (pages.length > 0 && safeToc.length > 0) {
    chapterTitles = resolveDisplayTitles(pages, safeToc);
  }

  if (chapterTitles.length === 0) {
    chapterTitles = getStoredChapterTitles(storedChapters);
  }

  return getAssignmentChaptersNumbers(chapterTitles);
}

export function getTabularData(tabularData = [], tocData = []) {
  if (!Array.isArray(tabularData) || tabularData.length === 0) {
    return [];
  }

  const safeToc = Array.isArray(tocData) ? tocData : [];

  return tabularData.map((item) => {
    const coverage = item?.textbookCoverage || {};
    const sectionIds = asCoverageArray(coverage.sectionIds);
    const storedChapters = asCoverageArray(coverage.chapters);
    const pages = asCoverageArray(coverage.pages);

    // Branch A — syllabus / toc-mapped: per-chapter full vs partial titles
    if (sectionIds.length > 0) {
      const badgeTitles = resolveCoverageBadgeTitles(coverage, safeToc);
      const nums = getAssignmentChaptersNumbers(badgeTitles);

      if (nums?.length > 0) {
        return { ...item, chapters: nums };
      }
    }

    // Branch B — manual selection via pages[]
    if (pages.length > 0) {
      const nums = getChapterNumbersFromPagesCoverage(coverage, safeToc);
      if (nums?.length > 0) {
        return { ...item, chapters: nums };
      }
    }

    // Branch C — fallback stored chapter titles only
    const chapters = getAssignmentChaptersNumbers(getStoredChapterTitles(storedChapters));

    return {
      ...item,
      chapters: chapters && chapters.length > 0 ? chapters : []
    };
  });
}
